/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback, memo } from 'react';
import { useTheme } from '../../../../Context/ThemeContext';
import { navItems, NavItem } from '../navConfig';
import { useNavigation } from '../hooks';
import { DarkModeToggle } from './DarkModeToggle';
import { SidebarAnimations } from './SidebarAnimations';
import mainLogo from '../../../../Assests/images/NavBarLogo.png';

interface SideSidebarProps {
  collapsed: boolean;
  setCollapsed: (value: boolean) => void;
  sidebarWidth: number;
  expandedItems: Record<string, boolean>;
  setExpandedItems: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  isMobile: boolean;
  setSidebarOpen: (value: boolean) => void;
}

// Icon component for better reusability
const NavIcon: React.FC<{ name?: string }> = memo(({ name }) => {
  switch (name) {
    case 'dashboard':
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="3" y="3" width="7" height="7" />
          <rect x="14" y="3" width="7" height="7" />
          <rect x="14" y="14" width="7" height="7" />
          <rect x="3" y="14" width="7" height="7" />
        </svg>
      );
    case 'users':
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      );
    case 'logout':
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
          <polyline points="16 17 21 12 16 7" />
          <line x1="21" y1="12" x2="9" y2="12" />
        </svg>
      );
    default:
      return (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="10" />
        </svg>
      );
  }
});

NavIcon.displayName = 'NavIcon';

export const SideSidebar: React.FC<SideSidebarProps> = memo(({
  collapsed,
  setCollapsed,
  sidebarWidth,
  expandedItems,
  setExpandedItems,
  isMobile,
  setSidebarOpen,
}) => {
  const { theme, darkMode, toggleDarkMode } = useTheme();
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [hoveredChild, setHoveredChild] = useState<string | null>(null);
  const [collapseButtonHovered, setCollapseButtonHovered] = useState(false);

  const { toggleExpanded, handleNavigate, isActive, isChildActive } = useNavigation({
    isMobile,
    setSidebarOpen,
    setExpandedItems,
  });

  // Cleanup timeout on unmount to prevent memory leak
  useEffect(() => {
    if (!isTransitioning) return;

    const timeoutId = setTimeout(() => {
      setIsTransitioning(false);
    }, 700);

    return () => clearTimeout(timeoutId);
  }, [isTransitioning]);

  const handleCollapseToggle = useCallback(() => {
    setIsTransitioning(true);
    setCollapsed(!collapsed);
  }, [collapsed, setCollapsed]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, action: () => void) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        action();
      }
    },
    []
  );

  const handleNavItemClick = useCallback(
    (item: NavItem) => {
      if (item.children) {
        toggleExpanded(item.label);
      } else if (item.path) {
        handleNavigate(item.path);
      }
    },
    [toggleExpanded, handleNavigate]
  );

  const handleCollapsedNavClick = useCallback(
    (item: NavItem) => {
      if (item.children) {
        handleNavigate(item.children[0].path);
      } else if (item.path) {
        handleNavigate(item.path);
      }
    },
    [handleNavigate]
  );

  const getCollapseButtonStyle = useCallback((): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {
      background: collapseButtonHovered && !isTransitioning ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.1)',
      border: 'none',
      color: '#fff',
      cursor: 'pointer',
      padding: '8px',
      fontSize: '16px',
      transition: 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'relative',
      zIndex: 1,
      width: '36px',
      height: '36px',
    };

    if (collapsed) {
      return {
        ...baseStyle,
        transform: collapseButtonHovered && !isTransitioning ? 'rotate(0deg) scale(1.1)' : 'rotate(0deg)',
      };
    }

    return {
      ...baseStyle,
      alignSelf: 'flex-start',
      marginBottom: '12px',
      transform: collapseButtonHovered && !isTransitioning ? 'translateX(-4px) scale(1.1)' : 'translateX(0) scale(1)',
    };
  }, [collapsed, collapseButtonHovered, isTransitioning]);

  const getNavItemStyle = useCallback(
    (item: NavItem, isHovered: boolean): React.CSSProperties => {
      const active = isActive(item);
      return {
        padding: '12px 16px',
        margin: '4px 8px',
        borderRadius: '8px',
        color: '#fff',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: active ? '#2563eb' : isHovered ? 'rgba(255,255,255,0.1)' : 'transparent',
        fontSize: '14px',
        fontWeight: 500,
        transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        position: 'relative',
        overflow: 'hidden',
        transform: isHovered && !active ? 'translateX(4px)' : 'translateX(0)',
        border: 'none',
        width: 'calc(100% - 16px)',
        textAlign: 'left',
      };
    },
    [isActive]
  );

  const getChildItemStyle = useCallback(
    (path: string, isHovered: boolean, isExpanded: boolean): React.CSSProperties => {
      const active = isChildActive(path);
      return {
        padding: '10px 16px 10px 32px',
        margin: '2px 8px',
        borderRadius: '6px',
        color: active ? '#fff' : '#94a3b8',
        background: active ? 'rgba(37,99,235,0.5)' : isHovered ? 'rgba(255,255,255,0.05)' : 'transparent',
        cursor: 'pointer',
        fontSize: '13px',
        transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        opacity: isExpanded ? 1 : 0,
        transform: isExpanded ? (isHovered ? 'translateX(4px)' : 'translateX(0)') : 'translateX(-15px)',
        border: 'none',
        width: 'calc(100% - 16px)',
        textAlign: 'left',
      };
    },
    [isChildActive]
  );

  const getCollapsedIconStyle = useCallback(
    (item: NavItem, isHovered: boolean): React.CSSProperties => {
      const active = isActive(item);
      return {
        width: '44px',
        height: '44px',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        background: active ? '#2563eb' : isHovered ? 'rgba(255,255,255,0.1)' : 'transparent',
        color: '#fff',
        transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        transform: isHovered && !active ? 'scale(1.1)' : 'scale(1)',
        border: 'none',
      };
    },
    [isActive]
  );

  return (
    <aside
      style={{
        position: 'fixed',
        left: 0,
        top: 0,
        bottom: 0,
        width: `${sidebarWidth}px`,
        height: '100vh',
        background: theme.sidebar,
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1000,
        transition: 'width 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        overflow: 'hidden',
        boxShadow: '2px 0 15px rgba(0,0,0,0.15)',
      }}
      onTransitionEnd={() => setIsTransitioning(false)}
      role="navigation"
      aria-label="Main navigation"
    >
      {/* Header */}
      <div style={{
        padding: '16px',
        borderBottom: `1px solid ${theme.border}`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: collapsed ? 'center' : 'flex-start',
        minHeight: '100px',
        flexShrink: 0,
        transition: 'all 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: collapsed
            ? 'linear-gradient(135deg, rgba(37, 99, 235, 0.1) 0%, transparent 100%)'
            : 'linear-gradient(135deg, rgba(37, 99, 235, 0.05) 0%, transparent 100%)',
          transition: 'all 0.6s ease',
          opacity: collapsed ? 0.7 : 0.5,
        }} />

        {collapsed ? (
          <>
            <img
              src={mainLogo}
              alt="Logo"
              style={{
                height: '32px',
                width: '32px',
                objectFit: 'contain',
                marginBottom: '12px',
                transition: 'all 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                opacity: collapsed ? 1 : 0,
                transform: collapsed ? 'scale(1) rotate(0deg)' : 'scale(0.8) rotate(-180deg)',
                position: 'relative',
                zIndex: 1,
              }}
            />
            <button
              onClick={handleCollapseToggle}
              onKeyDown={(e) => handleKeyDown(e, handleCollapseToggle)}
              onMouseEnter={() => setCollapseButtonHovered(true)}
              onMouseLeave={() => setCollapseButtonHovered(false)}
              disabled={isTransitioning}
              aria-label="Expand sidebar"
              aria-expanded={!collapsed}
              style={getCollapseButtonStyle()}
            >
              →
            </button>
          </>
        ) : (
          <>
            <button
              onClick={handleCollapseToggle}
              onKeyDown={(e) => handleKeyDown(e, handleCollapseToggle)}
              onMouseEnter={() => setCollapseButtonHovered(true)}
              onMouseLeave={() => setCollapseButtonHovered(false)}
              disabled={isTransitioning}
              aria-label="Collapse sidebar"
              aria-expanded={!collapsed}
              style={getCollapseButtonStyle()}
            >
              ←
            </button>
            <img
              src={mainLogo}
              alt="Logo"
              style={{
                height: '36px',
                width: 'auto',
                objectFit: 'contain',
                alignSelf: 'center',
                transition: 'all 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                opacity: collapsed ? 0 : 1,
                transform: collapsed ? 'scale(0.8) rotate(180deg)' : 'scale(1) rotate(0deg)',
                position: 'relative',
                zIndex: 1,
              }}
            />
          </>
        )}
      </div>

      {/* Collapsed Navigation Icons */}
      {collapsed && (
        <nav style={{
          padding: '8px 0',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '4px',
          opacity: collapsed ? 1 : 0,
          transition: 'opacity 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          transitionDelay: collapsed ? '0.2s' : '0s',
        }} aria-label="Collapsed navigation">
          {navItems.map((item) => (
            <button
              key={item.label}
              onClick={() => handleCollapsedNavClick(item)}
              onKeyDown={(e) => handleKeyDown(e, () => handleCollapsedNavClick(item))}
              onMouseEnter={() => setHoveredItem(`collapsed-${item.label}`)}
              onMouseLeave={() => setHoveredItem(null)}
              title={item.label}
              aria-label={item.label}
              style={getCollapsedIconStyle(item, hoveredItem === `collapsed-${item.label}`)}
            >
              <NavIcon name={item.icon} />
            </button>
          ))}
        </nav>
      )}

      {/* Navigation */}
      <nav style={{
        flex: 1,
        padding: '8px 0',
        overflowY: 'auto',
        overflowX: 'hidden',
        opacity: collapsed ? 0 : 1,
        transform: collapsed ? 'translateX(-20px) scale(0.98)' : 'translateX(0) scale(1)',
        transition: 'opacity 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94), transform 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        transitionDelay: collapsed ? '0s' : '0.2s',
        pointerEvents: collapsed ? 'none' : 'auto',
        visibility: collapsed ? 'hidden' : 'visible',
      }} aria-label="Main navigation menu">
        {navItems.map((item, index) => (
          <div
            key={item.label}
            style={{
              transform: collapsed ? 'translateX(-15px)' : 'translateX(0)',
              opacity: collapsed ? 0 : 1,
              transition: `transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${collapsed ? '0s' : `${0.1 + index * 0.05}s`},
                         opacity 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) ${collapsed ? '0s' : `${0.1 + index * 0.05}s`}`,
            }}
          >
            <button
              onClick={() => handleNavItemClick(item)}
              onKeyDown={(e) => handleKeyDown(e, () => handleNavItemClick(item))}
              onMouseEnter={() => setHoveredItem(item.label)}
              onMouseLeave={() => setHoveredItem(null)}
              aria-expanded={item.children ? expandedItems[item.label] : undefined}
              aria-haspopup={item.children ? 'true' : undefined}
              style={getNavItemStyle(item, hoveredItem === item.label)}
            >
              <span style={{ position: 'relative', zIndex: 1 }}>{item.label}</span>
              {item.children && (
                <span
                  style={{
                    fontSize: '10px',
                    transition: 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                    transform: expandedItems[item.label] ? 'rotate(180deg)' : 'rotate(0deg)',
                    display: 'inline-block',
                    position: 'relative',
                    zIndex: 1,
                  }}
                  aria-hidden="true"
                >
                  ▼
                </span>
              )}
            </button>
            {item.children && (
              <div
                style={{
                  maxHeight: expandedItems[item.label] ? '500px' : '0',
                  overflow: 'hidden',
                  opacity: expandedItems[item.label] ? 1 : 0,
                  transform: expandedItems[item.label] ? 'translateX(0)' : 'translateX(-10px)',
                  transition: 'max-height 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.4s ease, transform 0.4s ease',
                }}
                role="menu"
                aria-label={`${item.label} submenu`}
              >
                {item.children.map((child, childIndex) => (
                  <button
                    key={child.path}
                    onClick={() => handleNavigate(child.path)}
                    onKeyDown={(e) => handleKeyDown(e, () => handleNavigate(child.path))}
                    onMouseEnter={() => setHoveredChild(child.path)}
                    onMouseLeave={() => setHoveredChild(null)}
                    role="menuitem"
                    style={{
                      ...getChildItemStyle(child.path, hoveredChild === child.path, !!expandedItems[item.label]),
                      transitionDelay: expandedItems[item.label] ? `${childIndex * 0.05}s` : '0s',
                    }}
                  >
                    {child.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </nav>

      {/* Spacer */}
      <div style={{
        flex: 1,
        transition: 'flex 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        minHeight: collapsed ? '20px' : '0',
      }} />

      {/* Dark Mode Toggle */}
      <div style={{
        padding: collapsed ? '12px' : '16px',
        borderTop: `1px solid ${theme.border}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'space-between',
        flexShrink: 0,
        transition: 'all 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        position: 'relative',
        overflow: 'hidden',
        height: collapsed ? '60px' : 'auto',
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: collapsed
            ? 'linear-gradient(0deg, rgba(0,0,0,0.2) 0%, transparent 100%)'
            : 'linear-gradient(0deg, rgba(0,0,0,0.1) 0%, transparent 100%)',
          opacity: collapsed ? 0.7 : 0.5,
          transition: 'all 0.5s ease',
        }} />

        {!collapsed && (
          <div style={{
            position: 'relative',
            zIndex: 1,
            opacity: collapsed ? 0 : 1,
            transform: collapsed ? 'translateX(-10px)' : 'translateX(0)',
            transition: 'all 0.4s ease',
          }}>
            <span style={{ color: '#fff', fontSize: '13px' }}>Dark Mode</span>
          </div>
        )}

        <div style={{
          position: 'relative',
          zIndex: 1,
          transform: collapsed ? 'scale(0.9)' : 'scale(1)',
          transition: 'all 0.4s ease',
        }}>
          <DarkModeToggle
            darkMode={darkMode}
            toggleDarkMode={toggleDarkMode}
            includeLabel={false}
            style={{ margin: collapsed ? '0 auto' : '0' }}
          />
        </div>
      </div>

      <SidebarAnimations />
    </aside>
  );
});

SideSidebar.displayName = 'SideSidebar';
